import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import i18n from "../../config/i18n";
import { adaptiveStudyAPI } from "../../services/adaptiveStudyAPI";
import { toApiError } from "../../utils/errorUtils";

import type {
  AdaptiveQuestion,
  AdaptiveStudySettings,
  AdaptiveStudyMode,
  AnswerQuality,
} from "../../types/adaptive";
import type { PayloadAction } from "@reduxjs/toolkit";

interface SubmitAnswerPayload {
  wordId: string;
  mode: AdaptiveStudyMode;
  isCorrect: boolean;
  selectedAnswer: string;
  answerQuality: AnswerQuality;
  responseTime: number;
  hintsUsed: number;
}

interface AdaptiveStudyState {
  currentQuestion: AdaptiveQuestion | null;
  questionsAnswered: number;
  correctAnswers: number;
  reviewedWordIds: string[];
  maxQuestions: number;
  sessionActive: boolean;
  sessionFinished: boolean;
  forceLearning: boolean;
  loading: boolean;
  error: string | null;
  noWords: boolean;
  autoAdvance: boolean;
  questionRequestId: string | null;
  answerRequestId: string | null;
  answer: SubmitAnswerPayload | null;
  answerStatus: "idle" | "saving" | "saved" | "error";
  settings: AdaptiveStudySettings;
}

const defaultSettings: AdaptiveStudySettings = {
  preferredMode: undefined,
  forceLearning: false,
  maxQuestions: 20,
  timeLimit: undefined,
  difficultyLevel: "adaptive",
  enableHints: true,
  repeatWrongAnswers: true,
  showProgress: true,
};
const initialState: AdaptiveStudyState = {
  currentQuestion: null,
  questionsAnswered: 0,
  correctAnswers: 0,
  reviewedWordIds: [],
  maxQuestions: 20,
  sessionActive: false,
  sessionFinished: false,
  forceLearning: false,
  loading: false,
  error: null,
  noWords: false,
  autoAdvance: true,
  questionRequestId: null,
  answerRequestId: null,
  answer: null,
  answerStatus: "idle",
  settings: defaultSettings,
};
type StudyRootState = { adaptiveStudy: AdaptiveStudyState };

export const fetchNextQuestion = createAsyncThunk<
  AdaptiveQuestion | null,
  { forceLearning?: boolean } | undefined,
  { rejectValue: string; state: StudyRootState }
>(
  "adaptive/fetchNextQuestion",
  async (params, { getState, rejectWithValue }) => {
    try {
      const response = await adaptiveStudyAPI.getQuestion({
        forceLearning:
          params?.forceLearning ?? getState().adaptiveStudy.forceLearning,
      });
      if (!response.success)
        return rejectWithValue(
          response.error || i18n.t("common:errors.failedToFetchQuestion"),
        );
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
  {
    condition: (_, { getState }): boolean => {
      const state = getState().adaptiveStudy;
      return (
        state.sessionActive &&
        !state.loading &&
        state.answerStatus !== "saving" &&
        state.questionsAnswered < state.maxQuestions
      );
    },
  },
);

export const submitAnswer = createAsyncThunk<
  { word: { id: string } },
  SubmitAnswerPayload,
  { rejectValue: string; state: StudyRootState }
>(
  "adaptive/submitAnswer",
  async (answer, { rejectWithValue }) => {
    try {
      const response = await adaptiveStudyAPI.submitResult(answer);
      if (!response.success || !response.data?.word)
        return rejectWithValue(
          response.error || i18n.t("common:errors.failedToSubmitAnswer"),
        );
      return { word: { id: response.data.word.id } };
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
  {
    condition: (answer, { getState }): boolean => {
      const state = getState().adaptiveStudy;
      return (
        state.sessionActive &&
        state.currentQuestion?.word.id === answer.wordId &&
        state.answerStatus !== "saving" &&
        state.answerStatus !== "saved"
      );
    },
  },
);

const adaptiveStudySlice = createSlice({
  name: "adaptiveStudy",
  initialState,
  reducers: {
    setAutoAdvance: (state, action: PayloadAction<boolean>): void => {
      state.autoAdvance = action.payload;
    },
    startSession: (
      state,
      action: PayloadAction<{ forceLearning?: boolean } | undefined>,
    ): void => {
      state.sessionActive = true;
      state.sessionFinished = false;
      state.questionsAnswered = 0;
      state.correctAnswers = 0;
      state.reviewedWordIds = [];
      state.currentQuestion = null;
      state.error = null;
      state.noWords = false;
      state.forceLearning = action.payload?.forceLearning ?? false;
      state.settings.forceLearning = state.forceLearning;
      state.loading = false;
      state.questionRequestId = null;
      state.answerRequestId = null;
      state.answer = null;
      state.answerStatus = "idle";
    },
    endSession: (state): void => {
      state.sessionActive = false;
      state.sessionFinished = true;
      state.error = null;
      state.currentQuestion = null;
      state.loading = false;
      state.questionRequestId = null;
    },
    enableForceLearning: (state): void => {
      state.forceLearning = true;
    },
    setMaxQuestions: (state, action: PayloadAction<number>): void => {
      state.maxQuestions = action.payload;
      state.settings.maxQuestions = action.payload;
    },
    updateSettings: (
      state,
      action: PayloadAction<Partial<AdaptiveStudySettings>>,
    ): void => {
      state.settings = { ...state.settings, ...action.payload };
      if (action.payload.maxQuestions)
        state.maxQuestions = action.payload.maxQuestions;
      if (typeof action.payload.forceLearning === "boolean")
        state.forceLearning = action.payload.forceLearning;
    },
    clearError: (state): void => {
      state.error = null;
    },
    resetAdaptiveStudyState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNextQuestion.pending, (state, action): void => {
        state.loading = true;
        state.questionRequestId = action.meta.requestId;
        state.currentQuestion = null;
        state.error = null;
        state.noWords = false;
        state.answer = null;
        state.answerStatus = "idle";
      })
      .addCase(fetchNextQuestion.fulfilled, (state, action): void => {
        if (state.questionRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.currentQuestion = action.payload;
        state.noWords = action.payload === null;
        state.questionRequestId = null;
      })
      .addCase(fetchNextQuestion.rejected, (state, action): void => {
        if (state.questionRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.error =
          action.payload ??
          action.error.message ??
          i18n.t("common:errors.failedToFetchQuestion");
        state.questionRequestId = null;
      })
      .addCase(submitAnswer.pending, (state, action): void => {
        state.answerRequestId = action.meta.requestId;
        state.answer = action.meta.arg;
        state.answerStatus = "saving";
      })
      .addCase(submitAnswer.fulfilled, (state, action): void => {
        if (state.answerRequestId !== action.meta.requestId) return;
        state.answerStatus = "saved";
        state.answerRequestId = null;
        state.questionsAnswered += 1;
        if (action.meta.arg.isCorrect) state.correctAnswers += 1;
        if (!state.reviewedWordIds.includes(action.meta.arg.wordId))
          state.reviewedWordIds.push(action.meta.arg.wordId);
      })
      .addCase(submitAnswer.rejected, (state, action): void => {
        if (state.answerRequestId !== action.meta.requestId) return;
        state.answerStatus = "error";
        state.answerRequestId = null;
      });
  },
});
export const {
  startSession,
  setAutoAdvance,
  endSession,
  enableForceLearning,
  setMaxQuestions,
  clearError,
  updateSettings,
  resetAdaptiveStudyState,
} = adaptiveStudySlice.actions;
export const selectAdaptiveQuestion = (
  state: StudyRootState,
): AdaptiveStudyState["currentQuestion"] => state.adaptiveStudy.currentQuestion;
export const selectAdaptiveSettings = (
  state: StudyRootState,
): AdaptiveStudyState["settings"] => state.adaptiveStudy.settings;
export const selectAdaptiveLoading = (state: StudyRootState): boolean =>
  state.adaptiveStudy.loading;
export const selectAdaptiveError = (state: StudyRootState): string | null =>
  state.adaptiveStudy.error;
export default adaptiveStudySlice.reducer;
